/**
 * Ingest-thumb vs full raster for the freeze-viewer canvas.
 * Host packages ship full images only; ingest writes `{id}_thumb` (max edge 720).
 */
import type { PublicShareManifest, SnapshotMediaRef } from '@containd/shared';
import { isAnimatedSharePath } from './animatedSharePath';
import {
  findShareMedia,
  resolveShareMediaUrl,
  resolveShareMediaUrlByPath,
} from './shareMediaUrl';

/** Match `functions/.../renderShareThumb.ts` SHARE_THUMB_MAX_EDGE. */
export const SHARE_INGEST_THUMB_MAX_EDGE = 720;

export function shouldPreferCanvasFullPhoto(
  edgeCssPx: number,
  devicePixelRatio = 1,
): boolean {
  const dpr =
    Number.isFinite(devicePixelRatio) && devicePixelRatio > 0
      ? devicePixelRatio
      : 1;
  return edgeCssPx * dpr > SHARE_INGEST_THUMB_MAX_EDGE;
}

export function preferCanvasThumbForItem(
  width: number,
  height: number,
  zoom: number,
  devicePixelRatio = 1,
): boolean {
  const edge = Math.max(width, height) * zoom;
  return !shouldPreferCanvasFullPhoto(edge, devicePixelRatio);
}

/** Sibling ingest thumb URL for a full raster, or null if none is listed. */
export function resolveIngestThumbUrl(
  manifest: PublicShareManifest,
  fullMedia: Pick<SnapshotMediaRef, 'id' | 'path'>,
): string | null {
  const byId = manifest.media.find((x) => x.id === `${fullMedia.id}_thumb`);
  if (byId) {
    return resolveShareMediaUrl(manifest, byId);
  }
  const siblingJpg = fullMedia.path.replace(/\.(gif|webp)$/i, '_thumb.jpg');
  const siblingSameExt = fullMedia.path.replace(/(\.[^.]+)$/, '_thumb$1');
  const thumb =
    manifest.media.find((x) => x.path === siblingJpg) ||
    manifest.media.find((x) => x.path === siblingSameExt) ||
    manifest.media.find(
      (x) => x.kind === 'thumb' && x.id === `${fullMedia.id}_thumb`,
    );
  return thumb ? resolveShareMediaUrl(manifest, thumb) : null;
}

/**
 * Canvas / baked-descriptor raster URL.
 * - SVG: always the packaged file (no ingest thumb).
 * - GIF/WebP + animateImages: full so `<img>` can loop (DEC-083).
 * - GIF/WebP + animation off: ingest still thumb (even when the face is large).
 * - Other rasters: ingest thumb when `preferThumb`, else full.
 */
export function resolveShareCanvasRasterUrl(
  manifest: PublicShareManifest,
  mediaIdOrPath: string,
  options?: { preferThumb?: boolean; animateImages?: boolean },
): string | null {
  const media = findShareMedia(manifest, mediaIdOrPath);
  if (!media) {
    return resolveShareMediaUrlByPath(manifest, mediaIdOrPath);
  }
  const full = resolveShareMediaUrl(manifest, media);
  if (!full) return null;
  if (/\.svg(?:$|\?)/i.test(media.path)) return full;

  const animated = isAnimatedSharePath(media.path);
  const animate = options?.animateImages === true;
  if (animated && animate) return full;
  if (animated && !animate) {
    return resolveIngestThumbUrl(manifest, media) ?? full;
  }
  if (options?.preferThumb) {
    return resolveIngestThumbUrl(manifest, media) ?? full;
  }
  return full;
}
